import type { FC } from "react"

import { useRecoilValue } from "recoil"

import useFetchProposalPricing from "~/api/hooks/useFetchProposalPricing"
import { useDesignContext } from "~/containers/Proposal/state/designs/design-context"
import { useProposalContext } from "~/containers/Proposal/state/proposal/proposal-context"
import {
  currentPermissionsState,
  currentUserMinimumMarginState,
} from "~/containers/Proposal/state/user/user-state"
import { VISION_DESIGN_PERMISSION } from "~/utils/canManipulateDesign"
import {
  MAX_PROPOSAL_MARGIN,
  MIN_PROPOSAL_MARGIN,
} from "~/utils/finance/constants"
import { isPowurLineProject } from "~/utils/powurline-utils"

import EnterpriseSystemPricing from "./EnterprisePricing"
import NonEnterpriseSystemPricingRegular from "./NonEnteprisePricing"
import SystemPricingEmpty from "./SystemPricingEmpty"
import SystemPricingLoading from "./SystemPricingLoading"
import type { SystemPricingItem } from "./utils/system-pricings.utils"
import {
  consolidateEquipmentItems,
  mapPowurDealerFeeToSystemPricingLineItem,
  mapPowurOsAddersPricingToSystemPricingLineItem,
  mapPowurOsFeesToSystemPricingLineItem,
  mapPowurOsRebatesToSystemPricingLineItem,
  mapPowurOsWarrantyToSystemPricingLineItem,
  splitEquipmentForPowurLine,
} from "./utils/system-pricings.utils"

const SystemPricing: FC<{ isPdf?: boolean }> = ({ isPdf = false }) => {
  const { local: proposal } = useProposalContext()
  const { local: design } = useDesignContext()

  const minimumMargin =
    useRecoilValue(currentUserMinimumMarginState) ?? MIN_PROPOSAL_MARGIN

  const currentPermissions = useRecoilValue(currentPermissionsState)

  const { data, isLoading, error } = useFetchProposalPricing(proposal.id)

  if (isLoading) {
    return <SystemPricingLoading />
  }

  if (error) {
    return <SystemPricingEmpty />
  }

  const maxMargin = MAX_PROPOSAL_MARGIN
  const editableMarginRate = proposal.margin

  const isAdmin = currentPermissions.some(
    (permission) =>
      permission === VISION_DESIGN_PERMISSION.vision_admin ||
      permission === VISION_DESIGN_PERMISSION.vision_proposal_admin ||
      permission === VISION_DESIGN_PERMISSION.vision_developer,
  )
  const isEnterprise = !!proposal.enterprise
  const isPowurLine = isPowurLineProject(proposal.enterpriseSettings)

  const baseAdders = mapPowurOsAddersPricingToSystemPricingLineItem(
    data?.adders ?? [],
    data?.systemSize ?? 1,
  )

  let equipmentItems: SystemPricingItem[]
  let equipmentAsAdders: SystemPricingItem[] = []

  if (isPowurLine) {
    const split = splitEquipmentForPowurLine(
      data?.equipment ?? [],
      data?.adders ?? [],
      data?.systemSize ?? 1,
    )
    equipmentItems = split.equipmentItems
    equipmentAsAdders = split.equipmentAsAdders
  } else {
    equipmentItems = consolidateEquipmentItems(
      data?.equipment ?? [],
      data?.adders ?? [],
      data?.systemSize ?? 1,
    )
  }

  const feeItems = mapPowurOsFeesToSystemPricingLineItem(
    data?.fees ?? [],
    data?.systemSize ?? 1,
  )

  const rebateItems = mapPowurOsRebatesToSystemPricingLineItem(
    data?.rebates ?? [],
    data?.systemSize ?? 1,
  )

  const displayItems = [
    ...baseAdders,
    ...equipmentItems,
    ...equipmentAsAdders,
    ...feeItems,
    ...rebateItems,
  ]

  if (data?.dealerFee || data?.powurLenderMarginFee) {
    displayItems.push(
      mapPowurDealerFeeToSystemPricingLineItem(
        data.dealerFee,
        data.dealerFeeRate,
        data.powurLenderMarginFee ?? 0,
        data.powurLenderMarginRate ?? 0,
        data.fundingTypeId,
        data.fundingType,
        data.systemSize,
      ),
    )
  }

  if (design) {
    const warrantyLineItem: SystemPricingItem | null =
      mapPowurOsWarrantyToSystemPricingLineItem(
        data?.warranty ?? 0,
        {
          extendedWarrantyPowurOsId: design.extendedWarrantyPowurOsId,
          extendedWarrantyName: design.extendedWarrantyName,
        },
        data?.systemSize ?? 1,
      )

    if (warrantyLineItem) {
      displayItems.push(warrantyLineItem)
    }
  }

  if (!isPowurLine && (isAdmin || isPdf)) {
    return (
      <NonEnterpriseSystemPricingRegular
        maxMargin={maxMargin}
        minimumMargin={minimumMargin}
        proposalId={proposal.id}
        variant="admin"
        proposalPrice={{
          marginRate: editableMarginRate,
          total: data?.totalContractPrice ?? 0,
          systemSize: data?.systemSize ?? 1,
          salesTax: data?.salesTax ?? 0,
          generalExciseTax: data?.generalExciseTax ?? 0,
          generalExciseTaxRate: data?.generalExciseTaxRate ?? 0,
          lineItems: displayItems,
          powurLinePricing: data?.powurLinePricing,
        }}
      />
    )
  }

  if (!isEnterprise) {
    return (
      <NonEnterpriseSystemPricingRegular
        maxMargin={maxMargin}
        minimumMargin={minimumMargin}
        proposalId={proposal.id}
        variant="non-enterprise"
        proposalPrice={{
          marginRate: editableMarginRate,
          total: data?.totalContractPrice ?? 0,
          systemSize: data?.systemSize ?? 0,
          salesTax: data?.salesTax ?? 0,
          generalExciseTax: data?.generalExciseTax ?? 0,
          generalExciseTaxRate: data?.generalExciseTaxRate ?? 0,
          lineItems: displayItems,
          powurLinePricing: data?.powurLinePricing,
        }}
      />
    )
  }

  return (
    <EnterpriseSystemPricing
      maxMargin={maxMargin}
      minimumMargin={minimumMargin}
      proposalId={proposal.id}
      variant="enterprise"
      isPowurLine={isPowurLine}
      proposalPrice={{
        marginRate: editableMarginRate,
        total: data?.totalContractPrice ?? 0,
        systemSize: data?.systemSize ?? 1,
        salesTax: data?.salesTax ?? 0,
        generalExciseTax: data?.generalExciseTax ?? 0,
        generalExciseTaxRate: data?.generalExciseTaxRate ?? 0,
        lineItems: displayItems,
        powurLinePricing: data?.powurLinePricing,
      }}
    />
  )
}

export default SystemPricing
